import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";
import { getRandomOpponent } from "./chessOpponents";
import "./ChessGame.css";
import toast from "react-hot-toast";
import axios from "axios";

const userAvatar = "https://i.pravatar.cc/150?img=5";

const PIECE_VALUES = {
    p: 100,
    n: 320,
    b: 330,
    r: 500,
    q: 900,
    k: 20000,
};

const BET_OPTIONS = [
    10,
    20,
    50,
    100,
    200,
    500,
    1000,
    5000,
    10000,
];

const API_URL = process.env.REACT_APP_API_URL || "";

export default function ChessGame() {
    // =====================================
    // CHESS GAME
    // =====================================

    const gameRef = useRef(new Chess());
    const gameCodeRef = useRef(null);

    const [position, setPosition] = useState(
        gameRef.current.fen()
    );

    const [gameStarted, setGameStarted] = useState(false);

    const [betAmount, setBetAmount] = useState(100);

    const [matching, setMatching] = useState(false);

    const [gameCode, setGameCode] = useState(null);

    const [moves, setMoves] = useState([]);

    const [thinking, setThinking] = useState(false);
    const [gameOver, setGameOver] = useState(false);

    const [gameStatus, setGameStatus] = useState("");
    const [winner, setWinner] = useState(null);
    const [wallet, setWallet] = useState(0);

    const [showResultPopup, setShowResultPopup] =
        useState(false);

    const [settlingGame, setSettlingGame] =
        useState(false);

    const [finalWinAmount, setFinalWinAmount] =
        useState(0);

    const tax = 10;
    const winAmountWithoutTax = betAmount * 2;
    const calWinTax = winAmountWithoutTax * 10 / 100;
    const winAmount = winAmountWithoutTax - calWinTax;

    // =====================================
    // CLICK TO MOVE
    // =====================================

    const [selectedSquare, setSelectedSquare] =
        useState(null);

    const [lastMove, setLastMove] = useState(null);

    const [showRules, setShowRules] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [chessHistory, setChessHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const [showReplay, setShowReplay] = useState(false);
    const [replayGame, setReplayGame] = useState(null);
    const [replayIndex, setReplayIndex] = useState(0);

    const [replayPosition, setReplayPosition] = useState(
        new Chess().fen()
    );
    const [replayPlaying, setReplayPlaying] = useState(false);

    const [optionSquares, setOptionSquares] =
        useState({});

    const [opponent, setOpponent] = useState(() =>
        getRandomOpponent()
    );

    const [showMenu, setShowMenu] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);

    // =====================================
    // CLEAR CLICK SELECTION
    // =====================================

    function clearSelection() {
        setSelectedSquare(null);
        setOptionSquares({});
    }

    /* 🔊 CHESS AUDIO */
    const audioFiles = useMemo(() => ({
        click: new Audio("/assets/chess/audio/click.mp3"),
        move: new Audio("/assets/chess/audio/click.mp3"),
        capture: new Audio("/assets/chess/audio/kill.mp3"),
        check: new Audio("/assets/chess/audio/open.mp3"),
        win: new Audio("/assets/chess/audio/open.mp3"),
        lose: new Audio("/assets/chess/audio/kill.mp3"),
        draw: new Audio("/assets/chess/audio/open.mp3"),
        matching: new Audio("/assets/chess/audio/open.mp3"),
    }), []);


    const playSound = useCallback((sound) => {

        const audio = audioFiles[sound];

        if (!audio) {
            return;
        }

        audio.currentTime = 0;

        audio.play().catch(() => { });

    }, [audioFiles]);

    useEffect(() => {

        if (!gameStarted) return;

        const chessSaveData = {
            fen: gameRef.current.fen(),

            moves,

            opponent,

            betAmount,

            gameCode,

            gameStarted,

            thinking,

            gameOver,

            gameStatus,

            winner,

            selectedSquare,

            timestamp: Date.now(),
        };

        localStorage.setItem(
            "prime777_chess_game",
            JSON.stringify(chessSaveData)
        );

    }, [
        position,
        moves,
        opponent,
        betAmount,
        gameCode,
        gameStarted,
        thinking,
        gameOver,
        gameStatus,
        winner,
        selectedSquare,
    ]);

    useEffect(() => {
        restoreActiveChessGame();
    }, []);

    const fetchUserBalance = async () => {

        try {

            const token =
                localStorage.getItem("token");

            if (!token) {
                return;
            }

            const response =
                await axios.get(
                    `${API_URL}/api/webapi/userInfo`,
                    {
                        headers: {
                            Authorization: token,
                        },
                    }
                );

            if (
                response.data?.status &&
                response.data?.data
            ) {

                const money =
                    Number(
                        response.data.data.money || 0
                    );

                setWallet(money);

            }

        } catch (error) {

            console.error(
                "FETCH CHESS WALLET ERROR:",
                error
            );

        }

    };

    useEffect(() => {
        fetchUserBalance();
    }, [API_URL]);

    function handleExitClick() {
        setShowMenu(false);

        // Game already finished → leave directly
        if (gameOver) {
            window.history.back();
            return;
        }

        // Computer is thinking → don't allow exit
        if (thinking) {
            toast.error("Please wait for the opponent's move.");
            return;
        }

        // Show our custom confirmation modal
        setShowExitConfirm(true);
    }

    async function confirmExitGame() {
        setShowExitConfirm(false);

        try {
            await saveChessGame(
                gameRef.current.fen(),
                moves,
                "ABANDONED",
                "lose"
            );

            const token = localStorage.getItem("token");

            await fetch(`${API_URL}/api/chess/finish`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                },
                body: JSON.stringify({
                    gameCode: gameCodeRef.current,
                    result: "lose",
                    fen: gameRef.current.fen(),
                    moves,
                    gameStatus: "ABANDONED",
                    winner: null,
                }),
            });

            localStorage.removeItem("prime777_chess_game");
            gameCodeRef.current = null;

            window.history.back();

        } catch (error) {
            console.error("EXIT GAME ERROR:", error);
            toast.error("Unable to exit game. Please try again.");
        }
    }

    async function fetchChessHistory() {
        try {
            setLoadingHistory(true);

            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_URL}/api/chess/history`,
                {
                    method: "GET",
                    headers: {
                        Authorization: token,
                    },
                }
            );

            const data = await response.json();

            if (!data.success) {
                toast.error(
                    data.message || "Unable to load Chess history"
                );
                return;
            }

            setChessHistory(data.data || []);
            setShowHistory(true);

        } catch (error) {
            console.error(
                "CHESS HISTORY ERROR:",
                error
            );

            toast.error(
                "Unable to load Chess history"
            );
        } finally {
            setLoadingHistory(false);
        }
    }

    async function startChessGame() {

        if (matching) {
            return;
        }

        try {

            setMatching(true);

            const token = localStorage.getItem("token");

            if (!token) {
                toast.error("Login to play!")
                return;
            }

            const response =
                await fetch(
                    `${API_URL}/api/chess/start`,
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json",

                            Authorization:
                                token,
                        },

                        body: JSON.stringify({
                            betAmount,
                        }),
                    }
                );

            const data =
                await response.json();


            // =====================================
            // API ERROR
            // =====================================

            if (!data.success) {

                toast.error(
                    data.message ||
                    "Unable to start Chess game"
                );

                setMatching(false);

                return;
            }


            // =====================================
            // RESTORE EXISTING ACTIVE GAME
            // Backend prevents double bet deduction
            // =====================================

            gameRef.current =
                new Chess(data.fen);


            setPosition(
                data.fen
            );


            setMoves(
                data.moves || []
            );


            setOpponent(
                data.opponent
            );


            setBetAmount(
                Number(data.betAmount)
            );


            setGameCode(
                data.gameCode
            );
            gameCodeRef.current = data.gameCode;


            setThinking(false);

            setGameOver(false);

            setGameStatus(
                data.gameStatus || ""
            );

            setWinner(
                data.winner || null
            );


            clearSelection();


            setGameStarted(true);

            setMatching(false);

            fetchUserBalance();


        } catch (error) {

            console.error(
                "CHESS START ERROR:",
                error
            );

            toast.error(
                "Unable to connect to server"
            );

            setMatching(false);

        }

    }

    async function saveChessGame(
        fen,
        updatedMoves,
        status,
        gameWinner
    ) {

        try {

            if (!gameCodeRef.current) {
                return;
            }

            const token =
                localStorage.getItem("token");


            await fetch(
                `${API_URL}/api/chess/save`,
                {
                    method: "POST",

                    headers: {
                        "Content-Type":
                            "application/json",

                        Authorization:
                            token,
                    },

                    body: JSON.stringify({

                        gameCode: gameCodeRef.current,

                        fen,

                        moves:
                            updatedMoves,

                        gameStatus:
                            status || "",

                        winner:
                            gameWinner || null,

                    }),
                }
            );

        } catch (error) {

            console.error(
                "CHESS SAVE ERROR:",
                error
            );

        }

    }

    async function restoreActiveChessGame() {

        try {

            const token =
                localStorage.getItem("token");


            if (!token) {
                return;
            }


            const response =
                await fetch(
                    `${API_URL}/api/chess/active`,
                    {
                        method: "GET",

                        headers: {
                            Authorization:
                                token,
                        },
                    }
                );


            const data =
                await response.json();


            if (
                !data.success ||
                !data.active ||
                !data.data
            ) {
                return;
            }


            const gameData =
                data.data;


            // Restore Chess.js

            gameRef.current =
                new Chess(
                    gameData.fen
                );


            setPosition(
                gameData.fen
            );


            setMoves(
                gameData.moves || []
            );


            setOpponent(
                gameData.opponent
            );


            setBetAmount(
                Number(
                    gameData.betAmount
                )
            );


            setGameCode(
                gameData.gameCode
            );
            gameCodeRef.current = gameData.gameCode;


            setGameStatus(
                gameData.gameStatus || ""
            );


            setWinner(
                gameData.winner || null
            );


            setGameOver(false);

            setThinking(false);

            setGameStarted(true);


            clearSelection();


            console.log(
                "ACTIVE CHESS GAME RESTORED"
            );

        } catch (error) {

            console.error(
                "RESTORE ACTIVE CHESS ERROR:",
                error
            );

        }

    }

    // =====================================
    // SHOW LEGAL MOVES
    // =====================================

    function getMoveOptions(square) {
        const game = gameRef.current;

        const possibleMoves = game.moves({
            square,
            verbose: true,
        });

        if (!possibleMoves.length) {
            clearSelection();
            return;
        }

        const styles = {};

        // Selected square
        styles[square] = {
            background:
                "rgba(255, 215, 70, 0.45)",
        };

        possibleMoves.forEach((move) => {
            const targetPiece = game.get(move.to);

            // Capture move
            if (targetPiece) {
                styles[move.to] = {
                    background:
                        "radial-gradient(circle, transparent 55%, rgba(255,80,80,.85) 57%, rgba(255,80,80,.85) 72%, transparent 74%)",
                };
            } else {
                // Normal legal move
                styles[move.to] = {
                    background:
                        "radial-gradient(circle, rgba(50,210,100,.85) 0%, rgba(50,210,100,.85) 22%, transparent 25%)",
                };
            }
        });

        setOptionSquares(styles);
    }

    // =====================================
    // AFTER USER MOVE
    // =====================================

    function afterUserMove(move) {

        // 🔊 USER MOVE SOUND
        if (move.captured) {
            playSound("capture");
        } else {
            playSound("move");
        }

        setLastMove({
            from: move.from,
            to: move.to,
        });

        const fen = gameRef.current.fen();

        const updatedMoves = [
            ...moves,
            {
                color: "w",
                san: move.san,
                from: move.from,
                to: move.to,
            },
        ];

        setPosition(fen);
        setMoves(updatedMoves);

        clearSelection();

        const finished = updateGameStatus(gameRef.current, updatedMoves);

        // 🔊 CHECK SOUND
        if (!finished && gameRef.current.isCheck()) {

            setTimeout(() => {
                playSound("check");
            }, 150);

        }

        // SAVE USER MOVE TO DATABASE
        saveChessGame(
            fen,
            updatedMoves,
            gameRef.current.isCheck()
                ? "CHECK"
                : "",
            null
        );

        if (finished) {
            return;
        }

        setThinking(true);

        setTimeout(() => {
            computerMove(updatedMoves);
        }, 700);
    }

    // =====================================
    // CLICK TO MOVE
    // =====================================

    function onSquareClick(square) {
        if (thinking || gameOver) {
            return;
        }

        const game = gameRef.current;

        // User always plays WHITE
        if (game.turn() !== "w") {
            return;
        }

        const clickedPiece = game.get(square);

        // =================================
        // FIRST CLICK
        // =================================

        if (!selectedSquare) {
            if (
                clickedPiece &&
                clickedPiece.color === "w"
            ) {
                // 🔊 TOKEN CLICK
                playSound("click");

                setSelectedSquare(square);
                getMoveOptions(square);
            }

            return;
        }

        // =================================
        // SAME SQUARE = DESELECT
        // =================================

        if (selectedSquare === square) {
            clearSelection();
            return;
        }

        // =================================
        // TRY MOVE
        // =================================

        try {
            const move = game.move({
                from: selectedSquare,
                to: square,
                promotion: "q",
            });

            if (move) {
                afterUserMove(move);
                return;
            }
        } catch (error) {
            // Invalid move
        }

        // =================================
        // SELECT ANOTHER WHITE PIECE
        // =================================

        if (
            clickedPiece &&
            clickedPiece.color === "w"
        ) {
            setSelectedSquare(square);
            getMoveOptions(square);
        } else {
            clearSelection();
        }
    }

    // =====================================
    // DRAG & DROP
    // =====================================

    function onPieceDrop(sourceSquare, targetSquare) {
        console.log(
            "DROP:",
            sourceSquare,
            targetSquare
        );

        if (thinking || gameOver) {
            return false;
        }

        const game = gameRef.current;

        // User only plays White
        if (game.turn() !== "w") {
            return false;
        }

        try {
            const move = game.move({
                from: sourceSquare,
                to: targetSquare,
                promotion: "q",
            });

            if (!move) {
                return false;
            }

            console.log("MOVE:", move.san);

            afterUserMove(move);

            return true;
        } catch (error) {
            console.log("INVALID MOVE");

            return false;
        }
    }

    // =====================================
    // BOARD EVALUATION
    // Positive = Black advantage
    // Negative = White advantage
    // =====================================

    function evaluateBoard(game) {
        // Checkmate
        if (game.isCheckmate()) {
            // White has been checkmated = Black wins
            if (game.turn() === "w") {
                return 1000000;
            }

            // Black has been checkmated
            return -1000000;
        }

        // Draw
        if (
            game.isDraw() ||
            game.isStalemate() ||
            game.isThreefoldRepetition() ||
            game.isInsufficientMaterial()
        ) {
            return 0;
        }

        let score = 0;

        const board = game.board();

        for (const row of board) {
            for (const piece of row) {
                if (!piece) continue;

                let value = PIECE_VALUES[piece.type];

                // Black positive
                if (piece.color === "b") {
                    score += value;
                } else {
                    score -= value;
                }
            }
        }

        // Small bonus for checking the opponent
        if (game.isCheck()) {
            if (game.turn() === "w") {
                score += 50;
            } else {
                score -= 50;
            }
        }

        return score;
    }


    // =====================================
    // MINIMAX + ALPHA BETA
    // =====================================

    function minimax(
        game,
        depth,
        alpha,
        beta,
        maximizingPlayer
    ) {
        if (depth === 0 || game.isGameOver()) {
            return evaluateBoard(game);
        }

        const moves = getOrderedMoves(game);

        // BLACK AI
        if (maximizingPlayer) {
            let bestValue = -Infinity;

            for (const move of moves) {
                const copy = new Chess(game.fen());

                copy.move({
                    from: move.from,
                    to: move.to,
                    promotion: move.promotion || "q",
                });

                const value = minimax(
                    copy,
                    depth - 1,
                    alpha,
                    beta,
                    false
                );

                bestValue = Math.max(
                    bestValue,
                    value
                );

                alpha = Math.max(
                    alpha,
                    value
                );

                if (beta <= alpha) {
                    break;
                }
            }

            return bestValue;
        }

        // WHITE PLAYER
        let bestValue = Infinity;

        for (const move of moves) {
            const copy = new Chess(game.fen());

            copy.move({
                from: move.from,
                to: move.to,
                promotion: move.promotion || "q",
            });

            const value = minimax(
                copy,
                depth - 1,
                alpha,
                beta,
                true
            );

            bestValue = Math.min(
                bestValue,
                value
            );

            beta = Math.min(
                beta,
                value
            );

            if (beta <= alpha) {
                break;
            }
        }

        return bestValue;
    }


    // =====================================
    // GET BEST BLACK MOVE
    // =====================================

    function getBestMove(game) {
        const possibleMoves = getOrderedMoves(game);

        if (!possibleMoves.length) {
            return null;
        }

        let bestValue = -Infinity;
        const scoredMoves = [];

        // HARD AI
        const depth = 3;

        for (const move of possibleMoves) {
            const copy = new Chess(game.fen());

            copy.move({
                from: move.from,
                to: move.to,
                promotion: move.promotion || "q",
            });

            const value = minimax(
                copy,
                depth - 1,
                -Infinity,
                Infinity,
                false
            );

            scoredMoves.push({
                move,
                value,
            });

            if (value > bestValue) {
                bestValue = value;
            }
        }

        /*
         * Don't always choose exactly one move.
         *
         * Allow several moves that are very close to
         * the best move. This makes the AI unpredictable
         * while keeping it strong.
         */
        const RANDOM_MARGIN = 25;

        const goodMoves = scoredMoves.filter(
            ({ value }) =>
                value >= bestValue - RANDOM_MARGIN
        );

        // Randomly select from strong candidate moves
        const selected =
            goodMoves[
            Math.floor(Math.random() * goodMoves.length)
            ];

        return selected.move;
    }

    function getOrderedMoves(game) {
        const moves = game.moves({ verbose: true });

        return moves.sort((a, b) => {
            let scoreA = 0;
            let scoreB = 0;

            // Prioritize captures
            if (a.captured) {
                scoreA += PIECE_VALUES[a.captured] * 10;
            }

            if (b.captured) {
                scoreB += PIECE_VALUES[b.captured] * 10;
            }

            // Promotions
            if (a.promotion) {
                scoreA += PIECE_VALUES[a.promotion] * 5;
            }

            if (b.promotion) {
                scoreB += PIECE_VALUES[b.promotion] * 5;
            }

            return scoreB - scoreA;
        });
    }

    // =====================================
    // HARD COMPUTER MOVE
    // =====================================

    function computerMove(currentMoves) {

        try {

            const game = gameRef.current;

            if (game.isGameOver()) {
                setGameOver(true);
                setThinking(false);
                return;
            }

            const bestMove =
                getBestMove(game);

            if (!bestMove) {
                setGameOver(true);
                setThinking(false);
                return;
            }

            const move = game.move({
                from: bestMove.from,
                to: bestMove.to,
                promotion: bestMove.promotion || "q",
            });

            // LAST MOVE
            setLastMove({
                from: move.from,
                to: move.to,
            });

            // 🔊 COMPUTER MOVE SOUND
            if (move.captured) {
                playSound("capture");
            } else {
                playSound("move");
            }

            const fen = game.fen();

            const updatedMoves = [
                ...currentMoves,
                {
                    color: "b",
                    san: move.san,
                    from: move.from,
                    to: move.to,
                },
            ];

            setPosition(fen);

            setMoves(updatedMoves);

            const finished = updateGameStatus(game, updatedMoves);

            // 🔊 COMPUTER GIVES CHECK
            if (!finished && game.isCheck()) {
                setTimeout(() => {
                    playSound("check");
                }, 150);
            }

            // SAVE COMPUTER MOVE TO DATABASE
            saveChessGame(
                fen,
                updatedMoves,
                game.isCheck()
                    ? "CHECK"
                    : "",
                null
            );

            setThinking(false);

            if (finished) {
                return;
            }

        } catch (error) {

            console.error(
                "HARD AI ERROR:",
                error
            );

            setThinking(false);
        }
    }

    function getKingSquare(game, color) {
        const board = game.board();

        for (let row = 0; row < 8; row++) {
            for (let col = 0; col < 8; col++) {
                const piece = board[row][col];

                if (
                    piece &&
                    piece.type === "k" &&
                    piece.color === color
                ) {
                    const file = "abcdefgh"[col];
                    const rank = 8 - row;

                    return `${file}${rank}`;
                }
            }
        }

        return null;
    }

    // =====================================
    // RESIGN
    // =====================================

    function handleResign() {
        clearSelection();
        setGameOver(true);
    }

    // =====================================
    // PLAY AGAIN
    // =====================================

    function resetGame() {

        gameRef.current =
            new Chess();

        gameCodeRef.current = null;

        setPosition(
            gameRef.current.fen()
        );

        setMoves([]);

        setThinking(false);

        setGameOver(false);

        setGameStatus("");

        setWinner(null);

        setGameStarted(false);

        setGameCode(null);

        setShowResultPopup(false);

        setFinalWinAmount(0);
        setLastMove(null);

        clearSelection();

        setOpponent(
            getRandomOpponent()
        );

        localStorage.removeItem(
            "prime777_chess_game"
        );
    }

    function updateGameStatus(game, currentMoves) {

        // ==========================
        // CHECKMATE
        // ==========================

        if (game.isCheckmate()) {

            // The player whose turn it is
            // is checkmated

            const winnerColor =
                game.turn() === "w"
                    ? "black"
                    : "white";

            const result =
                winnerColor === "white"
                    ? "win"
                    : "lose";

            setWinner(winnerColor);

            setGameStatus("CHECKMATE");

            setGameOver(true);


            if (result === "win") {

                playSound("win");

            } else {

                playSound("lose");

            }


            // Save final board first
            saveChessGame(
                game.fen(),
                currentMoves,
                "CHECKMATE",
                winnerColor
            );


            // Finish database game
            finishChessGame(
                result,
                "CHECKMATE",
                winnerColor,
                currentMoves
            );

            return true;
        }


        // ==========================
        // DRAW
        // ==========================

        if (game.isDraw()) {

            setWinner(null);

            setGameStatus("DRAW");

            setGameOver(true);

            playSound("draw");


            saveChessGame(
                game.fen(),
                currentMoves,
                "DRAW",
                null
            );


            finishChessGame(
                "draw",
                "DRAW",
                null,
                currentMoves
            );

            return true;
        }


        return false;
    }


    async function finishChessGame(
        result,
        status,
        winnerValue,
        currentMoves
    ) {

        if (
            settlingGame ||
            !gameCodeRef.current
        ) {
            return;
        }

        try {

            setSettlingGame(true);

            const token =
                localStorage.getItem("token");

            const response = await fetch(
                `${API_URL}/api/chess/finish`,
                {
                    method: "POST",

                    headers: {
                        "Content-Type":
                            "application/json",

                        Authorization: token,
                    },

                    body: JSON.stringify({

                        gameCode:
                            gameCodeRef.current,

                        result,

                        fen:
                            gameRef.current.fen(),

                        moves: currentMoves,

                        gameStatus:
                            status,

                        winner:
                            winnerValue,

                    }),
                }
            );

            const data =
                await response.json();

            console.log(
                "CHESS FINISH:",
                data
            );

            if (!data.success) {

                toast.error(
                    data.message ||
                    "Unable to finish game"
                );

                return;
            }

            // Update win amount from backend
            setFinalWinAmount(
                Number(data.winAmount || 0)
            );

            // Update wallet
            if (
                data.balance !== undefined
            ) {

                setWallet(
                    Number(data.balance)
                );

            }

            // Remove active game from localStorage
            localStorage.removeItem(
                "prime777_chess_game"
            );

            // Game is now finished
            // setGameStarted(false);

            // Wait 5 seconds so user can see final board
            setTimeout(() => {

                setShowResultPopup(true);

            }, 5000);

        } catch (error) {

            console.error(
                "FINISH CHESS ERROR:",
                error
            );

            toast.error(
                "Unable to finish Chess game"
            );

        } finally {

            setSettlingGame(false);

        }
    }

    // =====================================
    // RESULT MESSAGE
    // =====================================

    function getResultMessage() {

        if (gameRef.current.isCheckmate()) {
            if (gameRef.current.turn() === "w") {
                return `${opponent.name} wins the game.`;
            }

            return "Congratulations! You won the game!";
        }

        if (gameRef.current.isDraw()) {
            return "The game ended in a draw.";
        }

        return "The chess game has finished.";
    }

    const getCheckSquareStyles = () => {
        const game = gameRef.current;

        if (!game.isCheck()) {
            return {};
        }

        const board = game.board();

        for (let row = 0; row < board.length; row++) {
            for (let col = 0; col < board[row].length; col++) {

                const piece = board[row][col];

                if (
                    piece &&
                    piece.type === "k" &&
                    piece.color === game.turn()
                ) {
                    const file = String.fromCharCode(97 + col);
                    const rank = 8 - row;

                    const kingSquare = `${file}${rank}`;

                    return {
                        [kingSquare]: {
                            background:
                                "radial-gradient(circle, rgba(255,0,0,0.9) 0%, rgba(255,0,0,0.5) 45%, rgba(255,0,0,0.2) 100%)",
                        },
                    };
                }
            }
        }

        return {};
    };

    const checkSquareStyles = getCheckSquareStyles();

    const lastMoveStyles = lastMove
        ? {
            [lastMove.from]: {
                background:
                    "rgba(255, 215, 70, 0.35)",
            },
            [lastMove.to]: {
                background:
                    "rgba(255, 215, 70, 0.55)",
            },
        }
        : {};

    const boardSquareStyles = {
        ...lastMoveStyles,
        ...optionSquares,
        ...checkSquareStyles,
    };


    // =====================================
    // CHESS GAME REPLAY
    // =====================================

    // =====================================
    // CHESS GAME REPLAY
    // =====================================

    function buildReplayPosition(game, moveCount) {
        const chess = new Chess();

        const gameMoves = Array.isArray(game?.moves)
            ? game.moves
            : [];

        for (let i = 0; i < moveCount; i++) {
            const move = gameMoves[i];

            if (!move) {
                continue;
            }

            try {
                // Prefer from/to because your saved moves contain them.
                if (move.from && move.to) {
                    chess.move({
                        from: move.from,
                        to: move.to,
                        promotion: move.promotion || "q",
                    });
                }
                // Fallback for old games that only have SAN
                else if (move.san) {
                    chess.move(move.san);
                }
            } catch (error) {
                console.error("REPLAY MOVE ERROR:", {
                    index: i,
                    move,
                    error,
                });

                break;
            }
        }

        return chess.fen();
    }


    function openReplay(game) {
        if (!game) return;

        let parsedMoves = game.moves || [];

        if (typeof parsedMoves === "string") {
            try {
                parsedMoves = JSON.parse(parsedMoves);
            } catch (error) {
                console.error(
                    "Unable to parse replay moves:",
                    error
                );

                parsedMoves = [];
            }
        }

        const replayData = {
            ...game,
            moves: Array.isArray(parsedMoves)
                ? parsedMoves
                : [],
        };

        setReplayGame(replayData);
        setReplayIndex(0);
        setReplayPosition(new Chess().fen());

        setShowHistory(false);
        setShowReplay(true);
    }


    function goToReplayMove(index) {
        if (!replayGame) return;

        const totalMoves =
            replayGame.moves?.length || 0;

        const safeIndex = Math.max(
            0,
            Math.min(index, totalMoves)
        );

        const position = buildReplayPosition(
            replayGame,
            safeIndex
        );

        setReplayIndex(safeIndex);
        setReplayPosition(position);
    }


    function replayNext() {
        if (!replayGame) return;

        goToReplayMove(replayIndex + 1);
    }


    function replayPrevious() {
        if (!replayGame) return;

        goToReplayMove(replayIndex - 1);
    }


    function replayFirst() {
        goToReplayMove(0);
    }


    function replayLast() {
        if (!replayGame) return;

        goToReplayMove(
            replayGame.moves?.length || 0
        );
    }

    function toggleReplayPlay() {
        if (!replayGame) return;

        const totalMoves =
            replayGame.moves?.length || 0;

        // If replay reached the end, start again from beginning
        if (replayIndex >= totalMoves) {
            setReplayIndex(0);
            setReplayPosition(new Chess().fen());
            setReplayPlaying(true);
            return;
        }

        setReplayPlaying((prev) => !prev);
    }

    // =====================================
    // REPLAY AUTO PLAY
    // =====================================

    useEffect(() => {
        if (!replayPlaying || !replayGame) {
            return;
        }

        const totalMoves =
            replayGame.moves?.length || 0;

        // Stop automatically at the end
        if (replayIndex >= totalMoves) {
            setReplayPlaying(false);
            return;
        }

        const timer = setTimeout(() => {
            goToReplayMove(replayIndex + 1);
        }, 1000);

        return () => clearTimeout(timer);

    }, [
        replayPlaying,
        replayIndex,
        replayGame,
    ]);

    // =====================================
    // UI
    // =====================================


    if (!gameStarted) {
        return (
            <div className="chess-page">

                <div className="chess-start-card">

                    <div className="start-icon">
                        ♞
                    </div>

                    <h1>PLAY CHESS</h1>

                    <p>Select your bet amount</p>

                    {/* WALLET */}
                    <div className="bet-wallet-info">
                        <span>💰 WALLET BALANCE</span>

                        <strong>
                            ₹{Number(wallet).toLocaleString()}
                        </strong>
                    </div>

                    {/* BET OPTIONS */}
                    <div className="bet-options">
                        {BET_OPTIONS.map((amount) => (
                            <button
                                key={amount}
                                className={
                                    betAmount === amount
                                        ? "bet-option active"
                                        : "bet-option"
                                }
                                onClick={() => setBetAmount(amount)}
                            >
                                ₹{amount}
                            </button>
                        ))}
                    </div>

                    {/* SELECTED BET */}
                    {/* <div className="selected-bet">
                        <span>YOUR BET</span>

                        <strong>
                            ₹{Number(betAmount).toLocaleString()}
                        </strong>
                    </div> */}

                    {/* BET / WIN */}
                    <div className="bet-win-preview">
                        <div>
                            <span>BET</span>
                            <strong>
                                ₹{Number(betAmount).toLocaleString()}
                            </strong>
                        </div>

                        <div>
                            <span>POTENTIAL WIN</span>
                            <strong>
                                ₹{Number(winAmount).toLocaleString()}
                            </strong>
                        </div>
                    </div>

                    {/* HISTORY */}
                    <button
                        type="button"
                        className="bet-history-btn"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            fetchChessHistory();
                        }}
                        disabled={loadingHistory}
                    >
                        📜 {loadingHistory
                            ? "LOADING..."
                            : "VIEW HISTORY"}
                    </button>

                    {/* START GAME */}
                    <button
                        type="button"
                        className="start-chess-btn"
                        onClick={startChessGame}
                        disabled={matching}
                    >
                        {matching
                            ? "FINDING OPPONENT..."
                            : "START GAME"}
                    </button>

                </div>

                {/* HISTORY MODAL MUST BE HERE */}
                {showHistory && (
                    <div className="chess-modal-overlay">

                        <div className="chess-history-modal">

                            <button
                                className="modal-close"
                                onClick={() => setShowHistory(false)}
                            >
                                ×
                            </button>

                            <div className="modal-title">
                                📜
                                <h2>GAME HISTORY</h2>
                                <p>Your previous Chess games</p>
                            </div>

                            <div className="history-list">

                                {chessHistory.length === 0 ? (

                                    <div className="empty-history">
                                        <div>♟</div>
                                        <h3>No Games Yet</h3>

                                        <p>
                                            Your completed Chess games
                                            will appear here.
                                        </p>
                                    </div>

                                ) : (

                                    chessHistory.map((game) => (

                                        <div
                                            className="history-card"
                                            key={
                                                game.id ||
                                                game.game_code
                                            }
                                        >

                                            <div className="history-opponent">

                                                <img
                                                    src={game.opponent_avatar}
                                                    alt={game.opponent_name}
                                                />

                                                <div>
                                                    <strong>
                                                        {game.opponent_name}
                                                    </strong>

                                                    <small>
                                                        {game.game_code}
                                                    </small>
                                                </div>

                                            </div>

                                            <div className="history-result">

                                                <span
                                                    className={`history-result-badge ${game.result}`}
                                                >
                                                    {game.result === "win"
                                                        ? "WON"
                                                        : game.result === "lose"
                                                            ? "LOST"
                                                            : "DRAW"}
                                                </span>

                                                <small>
                                                    Bet ₹
                                                    {Number(
                                                        game.bet_amount
                                                    ).toLocaleString()}
                                                </small>

                                            </div>

                                            <div className="history-amount">

                                                <strong>
                                                    {game.result === "win"
                                                        ? `+₹${Number(
                                                            game.win_amount
                                                        ).toLocaleString()}`
                                                        : game.result === "draw"
                                                            ? `+₹${Number(
                                                                game.win_amount
                                                            ).toLocaleString()}`
                                                            : "₹0"}
                                                </strong>

                                                <small>
                                                    {game.finished_at
                                                        ? new Date(
                                                            game.finished_at
                                                        ).toLocaleString()
                                                        : ""}
                                                </small>

                                            </div>

                                            <button
                                                className="history-view-btn"
                                                onClick={() =>
                                                    openReplay(game)
                                                }
                                                disabled={
                                                    !game.moves ||
                                                    !game.moves.length
                                                }
                                            >
                                                ▶ VIEW GAME
                                            </button>

                                        </div>

                                    ))

                                )}

                            </div>

                        </div>

                    </div>
                )}



                {showReplay && replayGame && (
                    <div className="chess-modal-overlay">

                        <div className="chess-replay-modal">

                            <button
                                className="modal-close"
                                onClick={() => {
                                    setShowReplay(false);
                                    setReplayGame(null);
                                    setReplayIndex(0);
                                    setReplayPosition(new Chess().fen());
                                }}
                            >
                                ×
                            </button>

                            <div className="replay-header">

                                <div>
                                    <h2>GAME REPLAY</h2>

                                    <p>
                                        You vs {replayGame.opponent_name}
                                    </p>
                                </div>

                                <span
                                    className={`history-result-badge ${replayGame.result}`}
                                >
                                    {replayGame.result === "win"
                                        ? "YOU WON"
                                        : replayGame.result === "lose"
                                            ? "YOU LOST"
                                            : "DRAW"}
                                </span>

                            </div>


                            <div className="replay-board-wrapper">

                                <Chessboard
                                    id="ChessReplayBoard"
                                    position={replayPosition}
                                    boardOrientation="white"
                                    arePiecesDraggable={false}
                                    animationDuration={500}
                                />

                            </div>


                            <div className="replay-move-info">

                                <strong>
                                    {replayIndex === 0
                                        ? "START POSITION"
                                        : replayGame.moves?.[
                                            replayIndex - 1
                                        ]?.san || ""}
                                </strong>

                                <span>
                                    Move {replayIndex} /{" "}
                                    {replayGame.moves?.length || 0}
                                </span>

                            </div>


                            <div className="replay-controls">

                                {/* FIRST */}
                                <button
                                    onClick={() => {
                                        setReplayPlaying(false);
                                        replayFirst();
                                    }}
                                    disabled={replayIndex === 0}
                                >
                                    ⏮
                                </button>

                                {/* PREVIOUS */}
                                <button
                                    onClick={() => {
                                        setReplayPlaying(false);
                                        replayPrevious();
                                    }}
                                    disabled={replayIndex === 0}
                                >
                                    ◀
                                </button>

                                {/* AUTO PLAY / PAUSE */}
                                <button
                                    className="replay-play-btn"
                                    onClick={toggleReplayPlay}
                                >
                                    {replayPlaying ? "❚❚" : "▶"}
                                </button>

                                {/* NEXT */}
                                <button
                                    onClick={() => {
                                        setReplayPlaying(false);
                                        replayNext();
                                    }}
                                    disabled={
                                        replayIndex >=
                                        (replayGame.moves?.length || 0)
                                    }
                                >
                                    ▶
                                </button>

                                {/* LAST */}
                                <button
                                    onClick={() => {
                                        setReplayPlaying(false);
                                        replayLast();
                                    }}
                                    disabled={
                                        replayIndex >=
                                        (replayGame.moves?.length || 0)
                                    }
                                >
                                    ⏭
                                </button>

                            </div>


                            <div className="replay-move-list">

                                {(replayGame.moves || []).map(
                                    (move, index) => {

                                        const isActive =
                                            index === replayIndex - 1;

                                        return (
                                            <button
                                                key={`${move.from}-${move.to}-${index}`}
                                                className={
                                                    isActive
                                                        ? "replay-move active"
                                                        : "replay-move"
                                                }
                                                onClick={() => {
                                                    setReplayPlaying(false);
                                                    goToReplayMove(index + 1);
                                                }}
                                            >
                                                <span>
                                                    {Math.floor(index / 2) + 1}.
                                                </span>

                                                <strong>
                                                    {move.san}
                                                </strong>

                                                <small>
                                                    {move.from} → {move.to}
                                                </small>
                                            </button>
                                        );

                                    }
                                )}

                            </div>

                        </div>

                    </div>
                )}

            </div>
        );
    }

    return (
        <div className="chess-page">

            {/* HEADER */}

            <div className="chess-header">

                <button
                    className="header-btn menu-header-btn"
                    onClick={() => setShowMenu(prev => !prev)}
                    disabled={thinking}
                    aria-label="Open menu"
                >
                    ☰
                </button>


                <div className="chess-logo">
                    ♞ CHESS
                </div>


                <div className="chess-money-info">

                    <div className="header-money wallet-money">
                        <span>WALLET</span>

                        <strong>
                            ₹{Number(wallet).toLocaleString()}
                        </strong>
                    </div>


                    <div className="header-money bet-money">
                        <span>BET</span>

                        <strong>
                            ₹{Number(betAmount).toLocaleString()}
                        </strong>
                    </div>


                    <div className="header-money win-money">
                        <span>WIN</span>

                        <strong>
                            ₹{Number(winAmount).toLocaleString()}
                        </strong>
                    </div>

                </div>

            </div>


            {showMenu && (
                <div className="chess-menu">
                    <button
                        onClick={() => {
                            setShowMenu(false);
                            setShowRules(true);
                        }}
                    >
                        <span>♟</span>
                        <div>
                            <strong>Rules</strong>
                            <small>How to play Chess</small>
                        </div>
                    </button>

                    <button
                        onClick={() => {
                            setShowMenu(false);
                            fetchChessHistory();
                        }}
                        disabled={loadingHistory}
                    >
                        <span>📜</span>
                        <div>
                            <strong>History</strong>
                            <small>View previous games</small>
                        </div>
                    </button>

                    <button
                        className="menu-exit-btn"
                        onClick={handleExitClick}
                    >
                        <span>🚪</span>
                        <div>
                            <strong>Exit Game</strong>
                            <small>Leave this game</small>
                        </div>
                    </button>
                </div>
            )}


            {/* TURN */}

            <div className="turn-line">

                <span />

                <strong>
                    {gameOver
                        ? gameStatus || "GAME OVER"
                        : gameStatus === "CHECK"
                            ? gameRef.current.turn() === "w"
                                ? "⚠ YOUR KING IS IN CHECK!"
                                : `⚠ ${opponent.name.toUpperCase()} IS IN CHECK!`
                            : thinking
                                ? `${opponent.name.toUpperCase()} IS THINKING`
                                : "YOUR TURN"}
                </strong>

                <span />

            </div>


            {/* BOARD */}

            <div className="board-wrapper">

                <Chessboard
                    id="Prime777ChessBoard"
                    position={position}
                    onPieceDrop={onPieceDrop}
                    onSquareClick={onSquareClick}
                    customSquareStyles={boardSquareStyles}
                    boardOrientation="white"
                    animationDuration={250}
                    arePiecesDraggable={!thinking && !gameOver}
                />

            </div>

            {/* PLAYERS */}

            <div className="players-row">

                <div className="player-card">

                    <div className="avatar-wrap">
                        <img src={userAvatar} alt="You" />
                    </div>

                    <div className="player-info">
                        <h3>You</h3>
                        <p>White ♙</p>
                    </div>

                </div>


                <div className="vs-circle">
                    VS
                </div>


                <div className="player-card">

                    <div className="avatar-wrap">
                        <img
                            src={opponent.avatar}
                            alt={opponent.name}
                        />
                    </div>

                    <div className="player-info">
                        <h3>{opponent.name}</h3>
                        <p>Black ♟</p>
                    </div>

                </div>

            </div>


            {/* GAME OVER */}

            {showResultPopup && (

                <div className="game-result-overlay">

                    <div className="game-result-modal">

                        <div className="result-icon">

                            {winner === "white"
                                ? "🏆"
                                : winner === "black"
                                    ? "😔"
                                    : "🤝"}

                        </div>


                        <h2>

                            {winner === "white"
                                ? "YOU WON!"
                                : winner === "black"
                                    ? "YOU LOST"
                                    : "DRAW GAME"}

                        </h2>


                        <p>

                            {winner === "white" && (
                                <>
                                    You won ₹
                                    {finalWinAmount}
                                </>
                            )}

                            {winner === "black" && (
                                <>
                                    Better luck next time!
                                </>
                            )}

                            {!winner && (
                                <>
                                    The game ended in a draw.
                                </>
                            )}

                        </p>


                        <div className="result-buttons">

                            <button
                                className="play-again-btn"
                                onClick={() => {

                                    setShowResultPopup(false);

                                    resetGame();

                                }}
                            >
                                PLAY AGAIN
                            </button>


                            <button
                                className="cancel-game-btn"
                                onClick={() => {

                                    setShowResultPopup(false);

                                    // window.history.back();

                                }}
                            >
                                CANCEL
                            </button>

                        </div>

                    </div>

                </div>

            )}

            {showRules && (
                <div className="chess-modal-overlay">
                    <div className="chess-info-modal">

                        <button
                            className="modal-close"
                            onClick={() => setShowRules(false)}
                        >
                            ×
                        </button>

                        <div className="modal-title">
                            ♟
                            <h2>CHESS RULES</h2>
                            <p>How to play</p>
                        </div>

                        <div className="rules-content">

                            <div className="rule-item">
                                <strong>♔ King</strong>
                                <span>
                                    Moves one square in any direction.
                                </span>
                            </div>

                            <div className="rule-item">
                                <strong>♕ Queen</strong>
                                <span>
                                    Moves any number of squares
                                    horizontally, vertically or diagonally.
                                </span>
                            </div>

                            <div className="rule-item">
                                <strong>♖ Rook</strong>
                                <span>
                                    Moves horizontally or vertically.
                                </span>
                            </div>

                            <div className="rule-item">
                                <strong>♗ Bishop</strong>
                                <span>
                                    Moves diagonally.
                                </span>
                            </div>

                            <div className="rule-item">
                                <strong>♘ Knight</strong>
                                <span>
                                    Moves in an L-shape.
                                </span>
                            </div>

                            <div className="rule-item">
                                <strong>♙ Pawn</strong>
                                <span>
                                    Moves forward and captures diagonally.
                                </span>
                            </div>

                            <div className="rule-item">
                                <strong>⚠ Check</strong>
                                <span>
                                    Your king is under attack and
                                    must be protected.
                                </span>
                            </div>

                            <div className="rule-item">
                                <strong>♚ Checkmate</strong>
                                <span>
                                    The king is in check and has no
                                    legal move. The opponent wins.
                                </span>
                            </div>

                            <div className="rule-item">
                                <strong>🤝 Draw</strong>
                                <span>
                                    A game can end in a draw through
                                    stalemate, insufficient material,
                                    or other draw conditions.
                                </span>
                            </div>

                            <div className="rule-item">
                                <strong>💰 Betting</strong>
                                <span>
                                    Your selected bet is deducted when
                                    the game starts. A win pays the
                                    configured winning amount after tax.
                                    A draw refunds the original bet.
                                </span>
                            </div>

                        </div>

                        <button
                            className="modal-primary-btn"
                            onClick={() => setShowRules(false)}
                        >
                            GOT IT
                        </button>

                    </div>
                </div>
            )}

            {showHistory && (
                <div className="chess-modal-overlay">

                    <div className="chess-history-modal">

                        <button
                            className="modal-close"
                            onClick={() => setShowHistory(false)}
                        >
                            ×
                        </button>

                        <div className="modal-title">
                            📜
                            <h2>GAME HISTORY</h2>
                            <p>Your previous Chess games</p>
                        </div>

                        <div className="history-list">

                            {chessHistory.length === 0 ? (

                                <div className="empty-history">
                                    <div>♟</div>
                                    <h3>No Games Yet</h3>
                                    <p>
                                        Your completed Chess games
                                        will appear here.
                                    </p>
                                </div>

                            ) : (

                                chessHistory.map((game) => (

                                    <div
                                        className="history-card"
                                        key={game.id || game.game_code}
                                    >

                                        <div className="history-opponent">

                                            <img
                                                src={game.opponent_avatar}
                                                alt={game.opponent_name}
                                            />

                                            <div>
                                                <strong>
                                                    {game.opponent_name}
                                                </strong>

                                                <small>
                                                    {game.game_code}
                                                </small>
                                            </div>

                                        </div>


                                        <div className="history-result">

                                            <span
                                                className={`history-result-badge ${game.result}`}
                                            >
                                                {game.result === "win"
                                                    ? "WON"
                                                    : game.result === "lose"
                                                        ? "LOST"
                                                        : "DRAW"}
                                            </span>

                                            <small>
                                                Bet ₹
                                                {Number(
                                                    game.bet_amount
                                                ).toLocaleString()}
                                            </small>

                                        </div>


                                        <div className="history-amount">

                                            <strong>
                                                {game.result === "win"
                                                    ? `+₹${Number(
                                                        game.win_amount
                                                    ).toLocaleString()}`
                                                    : game.result === "draw"
                                                        ? `+₹${Number(
                                                            game.win_amount
                                                        ).toLocaleString()}`
                                                        : "₹0"}
                                            </strong>

                                            <small>
                                                {game.finished_at
                                                    ? new Date(
                                                        game.finished_at
                                                    ).toLocaleString()
                                                    : ""}
                                            </small>

                                        </div>


                                        <button
                                            className="history-view-btn"
                                            onClick={() =>
                                                openReplay(game)
                                            }
                                            disabled={
                                                !game.moves ||
                                                !game.moves.length
                                            }
                                        >
                                            ▶ VIEW GAME
                                        </button>

                                    </div>

                                ))

                            )}

                        </div>

                    </div>

                </div>
            )}

            {showReplay && replayGame && (
                <div className="chess-modal-overlay">

                    <div className="chess-replay-modal">

                        <button
                            className="modal-close"
                            onClick={() => {
                                setShowReplay(false);
                                setReplayGame(null);
                                setReplayIndex(0);
                                setReplayPosition(new Chess().fen());
                            }}
                        >
                            ×
                        </button>

                        <div className="replay-header">

                            <div>
                                <h2>GAME REPLAY</h2>

                                <p>
                                    You vs {replayGame.opponent_name}
                                </p>
                            </div>

                            <span
                                className={`history-result-badge ${replayGame.result}`}
                            >
                                {replayGame.result === "win"
                                    ? "YOU WON"
                                    : replayGame.result === "lose"
                                        ? "YOU LOST"
                                        : "DRAW"}
                            </span>

                        </div>


                        <div className="replay-board-wrapper">

                            <Chessboard
                                id="ChessReplayBoard"
                                position={replayPosition}
                                boardOrientation="white"
                                arePiecesDraggable={false}
                                animationDuration={500}
                            />

                        </div>


                        <div className="replay-move-info">

                            <strong>
                                {replayIndex === 0
                                    ? "START POSITION"
                                    : replayGame.moves?.[
                                        replayIndex - 1
                                    ]?.san || ""}
                            </strong>

                            <span>
                                Move {replayIndex} /{" "}
                                {replayGame.moves?.length || 0}
                            </span>

                        </div>


                        <div className="replay-controls">

                            {/* FIRST */}
                            <button
                                onClick={() => {
                                    setReplayPlaying(false);
                                    replayFirst();
                                }}
                                disabled={replayIndex === 0}
                            >
                                ⏮
                            </button>

                            {/* PREVIOUS */}
                            <button
                                onClick={() => {
                                    setReplayPlaying(false);
                                    replayPrevious();
                                }}
                                disabled={replayIndex === 0}
                            >
                                ◀
                            </button>

                            {/* AUTO PLAY / PAUSE */}
                            <button
                                className="replay-play-btn"
                                onClick={toggleReplayPlay}
                            >
                                {replayPlaying ? "❚❚" : "▶"}
                            </button>

                            {/* NEXT */}
                            <button
                                onClick={() => {
                                    setReplayPlaying(false);
                                    replayNext();
                                }}
                                disabled={
                                    replayIndex >=
                                    (replayGame.moves?.length || 0)
                                }
                            >
                                ▶
                            </button>

                            {/* LAST */}
                            <button
                                onClick={() => {
                                    setReplayPlaying(false);
                                    replayLast();
                                }}
                                disabled={
                                    replayIndex >=
                                    (replayGame.moves?.length || 0)
                                }
                            >
                                ⏭
                            </button>

                        </div>


                        <div className="replay-move-list">

                            {(replayGame.moves || []).map(
                                (move, index) => {

                                    const isActive =
                                        index === replayIndex - 1;

                                    return (
                                        <button
                                            key={`${move.from}-${move.to}-${index}`}
                                            className={
                                                isActive
                                                    ? "replay-move active"
                                                    : "replay-move"
                                            }
                                            onClick={() => {
                                                setReplayPlaying(false);
                                                goToReplayMove(index + 1);
                                            }}
                                        >
                                            <span>
                                                {Math.floor(index / 2) + 1}.
                                            </span>

                                            <strong>
                                                {move.san}
                                            </strong>

                                            <small>
                                                {move.from} → {move.to}
                                            </small>
                                        </button>
                                    );

                                }
                            )}

                        </div>

                    </div>

                </div>
            )}

            {showExitConfirm && (
                <div
                    className="chess-modal-overlay"
                    onClick={() => setShowExitConfirm(false)}
                >
                    <div
                        className="exit-confirm-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="exit-confirm-icon">
                            🚪
                        </div>

                        <h2>EXIT GAME?</h2>

                        <p>
                            Leaving this game will count as a loss.
                        </p>

                        <div className="exit-confirm-actions">
                            <button
                                className="stay-game-btn"
                                onClick={() => setShowExitConfirm(false)}
                            >
                                STAY IN GAME
                            </button>

                            <button
                                className="confirm-exit-btn"
                                onClick={confirmExitGame}
                            >
                                EXIT GAME
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}